import { Navigate } from 'react-router-dom';
import {
  AskQuestionForm,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  ErrorText,
  LinkedDocumentList,
  PersonDisplay,
  formatDate,
  useToast,
  type ChipTone,
  type IconName,
  type QuestionStage,
  type QuestionView,
} from '@dataroom/shared';
import {
  useAskSeller,
  useQuestionAction,
  useRoomDocuments,
  useRoomQuestions,
  type QuestionAction,
} from '@/api/hooks';
import { isUnauthorized } from '@/lib/checklist';
import { useRoomContext } from './context';

/** The buy side's view of where a question stands (draft_ready never occurs here). */
const BUY_SIDE_STAGES: QuestionStage[] = ['new', 'with_seller', 'answered', 'closed'];

const BUY_SIDE_STAGE_LABELS: Record<QuestionStage, string> = {
  new: 'Not sent — the seller changed',
  with_seller: 'Waiting for the seller',
  draft_ready: 'Waiting for the seller',
  answered: 'Answered',
  closed: 'Closed',
};

const STAGE_CHIP: Record<QuestionStage, { label: string; tone: ChipTone; icon: IconName }> = {
  new: { label: 'Not sent', tone: 'check', icon: 'help' },
  with_seller: { label: 'With the seller', tone: 'processing', icon: 'users' },
  draft_ready: { label: 'With the seller', tone: 'processing', icon: 'users' },
  answered: { label: 'Answered', tone: 'certified', icon: 'check' },
  closed: { label: 'Closed', tone: 'na', icon: 'archive' },
};

const TOAST: Partial<Record<QuestionAction['kind'], string>> = {
  route: 'Sent the question to the seller',
  close: 'Closed the question',
  reopen: 'Reopened the question',
};

/**
 * "Questions to the seller" — buyer Q&A in a BUYER-RUN room (ADR-0039
 * §"Buyer-run rooms"; rooms.admin_role = 'buyer'). The admin is the buy side:
 * they (and the team members they invited, from the room portal) ask, each
 * question goes straight to the bound seller, and the seller's answers land here
 * as soon as they are written. The admin sees every question the team asked, and
 * closes / reopens them. The seller sees the firm's name, not who asked.
 */
export function SellerQuestionsPanel() {
  const { roomId, room } = useRoomContext();
  const questions = useRoomQuestions(roomId);
  const documents = useRoomDocuments(roomId);
  const ask = useAskSeller(roomId);
  const action = useQuestionAction(roomId);
  const toast = useToast();

  if (isUnauthorized(questions.error)) return <Navigate to="/login" replace />;

  const hasSeller = Boolean(room.sellerActorId);
  const list = questions.data ?? [];
  const docs = (documents.data ?? []).map((d) => ({ id: d.id, name: d.name }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Questions to the seller</CardTitle>
        <p className="text-sm text-muted-foreground">
          Ask the seller about the business or a document. Everyone on your team sees every
          question here, and the seller’s answers arrive as soon as they’re written.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {hasSeller ? null : (
          <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground" role="note">
            Invite the seller to this room to send them questions.
          </p>
        )}
        <AskQuestionForm
          documents={docs}
          pending={ask.isPending}
          error={ask.error}
          disabled={!hasSeller}
          onAsk={(question, onDone) =>
            ask.mutate(question, {
              onSuccess: () => {
                toast({ message: 'Question sent to the seller' });
                onDone();
              },
            })
          }
          hint="The seller sees your firm’s name, not who on your team asked."
          sentMessage="Question sent to the seller."
        />

        {questions.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : questions.error ? (
          <ErrorText error={questions.error} />
        ) : list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No questions yet. Your team can also ask from the Questions tab of the room.
          </p>
        ) : (
          BUY_SIDE_STAGES.map((stage) => {
            const items = list.filter(
              (q) => (q.stage === 'draft_ready' ? 'with_seller' : q.stage) === stage,
            );
            if (items.length === 0) return null;
            return (
              <section
                key={stage}
                aria-label={BUY_SIDE_STAGE_LABELS[stage]}
                className="space-y-3"
              >
                <h3 className="text-sm font-semibold">
                  {BUY_SIDE_STAGE_LABELS[stage]} <span className="text-ink-3">({items.length})</span>
                </h3>
                <ul className="space-y-3">
                  {items.map((q) => {
                    const mine = action.variables?.questionId === q.id;
                    return (
                      <SellerQuestionItem
                        key={q.id}
                        question={q}
                        hasSeller={hasSeller}
                        pendingKind={mine && action.isPending ? action.variables.kind : null}
                        error={mine && !action.isPending ? action.error : null}
                        onAction={(a) => {
                          action.reset();
                          action.mutate(a, {
                            onSuccess: () => {
                              const message = TOAST[a.kind];
                              if (message) toast({ message });
                            },
                          });
                        }}
                      />
                    );
                  })}
                </ul>
              </section>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

function SellerQuestionItem({
  question: q,
  hasSeller,
  pendingKind,
  error,
  onAction,
}: {
  question: QuestionView;
  hasSeller: boolean;
  pendingKind: QuestionAction['kind'] | null;
  error: unknown;
  onAction: (action: QuestionAction) => void;
}) {
  const stage = q.stage ?? 'with_seller';
  const chip = STAGE_CHIP[stage];
  const busy = pendingKind !== null;
  // Open and not with the CURRENT seller: the seat was rebound — send it to the new seller.
  const canResend = q.status === 'open' && q.routedToSeller === false && hasSeller;

  return (
    <li className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <PersonDisplay
          name={q.buyer?.displayName}
          email={q.buyer?.email}
          size="sm"
          fallback="A team member"
        />
        <div className="flex flex-wrap items-center gap-2">
          <Chip tone={chip.tone} icon={chip.icon}>
            {chip.label}
          </Chip>
          <span className="text-xs text-muted-foreground">Asked {formatDate(q.createdAt)}</span>
        </div>
      </div>

      <p className="whitespace-pre-wrap break-words text-sm">{q.body}</p>
      {q.document || q.category ? (
        <p className="text-xs text-muted-foreground">
          About: {q.document ? q.document.name || 'Untitled document' : q.category?.name}
        </p>
      ) : null}

      {q.answer && q.answer.released ? (
        <div className="space-y-2 rounded-md bg-surface-2 p-3">
          <p className="text-xs font-semibold text-ink-2">
            Seller’s answer{q.answer.answeredAt ? ` · ${formatDate(q.answer.answeredAt)}` : ''}
          </p>
          <p className="whitespace-pre-wrap break-words text-sm">{q.answer.body}</p>
          <LinkedDocumentList documents={q.answer.documents} />
        </div>
      ) : q.status === 'open' && stage === 'with_seller' ? (
        <p className="text-xs text-muted-foreground">Waiting for the seller’s answer.</p>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        {canResend ? (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => onAction({ kind: 'route', questionId: q.id })}
          >
            {pendingKind === 'route' ? 'Sending…' : 'Send to the seller'}
          </Button>
        ) : null}
        {q.status === 'closed' ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => onAction({ kind: 'reopen', questionId: q.id })}
          >
            {pendingKind === 'reopen' ? 'Reopening…' : 'Reopen'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => onAction({ kind: 'close', questionId: q.id })}
          >
            {pendingKind === 'close' ? 'Closing…' : 'Close'}
          </Button>
        )}
      </div>
      <ErrorText error={error} />
    </li>
  );
}
